import { Options } from '@contentful/rich-text-react-renderer'
import { BLOCKS, MARKS } from '@contentful/rich-text-types'
import { clsx, type ClassValue } from 'clsx'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import React, { useEffect, useState } from 'react'
import { composeRenderProps } from 'react-aria-components'
import { extendTailwindMerge, twMerge } from 'tailwind-merge'
import { tv } from 'tailwind-variants'

export function classNames(
  ...classes: (false | null | undefined | string)[]
): string {
  return classes.filter(Boolean).join(' ')
}

const customTwMerge = extendTailwindMerge({
  extend: {
    theme: {
      spacing: [
        'xxxs',
        'xxs',
        'xs',
        's',
        'm',
        'l',
        'xl',
        'xxl',
        '15',
        '42',
        '46',
        '50',
        '54',
        '58',
        '80',
        '104',
        '108',
        '112',
        '116',
        '124',
        '128',
        '136',
        '144',
        '150',
        '158',
        '166',
        '168',
        '192',
        '224',
        '240',
        '256',
        '288',
        '320',
        '340',
        '360',
        '380',
        '400',
        '420',
      ],
    },
    classGroups: {
      'font-size': [
        {
          text: [
            'xxxxs',
            'xxxs',
            'xxs',
            'md',
            '2xl',
            '3xl',
            '4xl',
            '5xl',
            '6xl',
            '7xl',
            '8xl',
            '9xl',
          ],
        },
      ],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return customTwMerge(clsx(inputs))
}

export const useMultiKeyPress = () => {
  const [keysPressed, setKeyPressed] = useState([])
  function upHandler({ key }: { key: string }) {
    if (key === 'Escape') {
      setKeyPressed([])
      return
    }
    setKeyPressed((keysPressed) => [...keysPressed, key])
  }

  useEffect(() => {
    window.addEventListener('keyup', upHandler)
    return () => {
      window.removeEventListener('keyup', upHandler)
    }
  }, []) // Empty array ensures that effect is only run on mount and unmount

  return keysPressed
}

export const richTextOptions: Options = {
  renderMark: {
    [MARKS.BOLD]: (text) => <strong>{text}</strong>,
    [MARKS.ITALIC]: (text) => <em>{text}</em>,
  },
  renderNode: {
    [BLOCKS.HEADING_1]: (_node, children) => (
      <h1 className="mb-8 text-4xl font-normal text-default-100">{children}</h1>
    ),
    [BLOCKS.HEADING_2]: (_node, children) => {
      return (
        <h2 className="mb-8 text-3xl font-normal text-default-100">
          {children}
        </h2>
      )
    },
    [BLOCKS.HEADING_3]: (_node, children) => (
      <h3 className="mb-4 text-2xl font-normal">{children}</h3>
    ),
    [BLOCKS.HEADING_4]: (_node, children) => (
      <h4 className="mb-4 text-xl font-normal">{children}</h4>
    ),
    [BLOCKS.HEADING_5]: (_node, children) => (
      <h5 className="mb-4 text-xl font-normal">{children}</h5>
    ),
    [BLOCKS.HEADING_6]: (_node, children) => (
      <h6 className="mb-15 mt-4 text-md font-normal italic leading-relaxed text-default-600">
        {children}
      </h6>
    ),
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <p className="mb-4 text-lg leading-loose">{children}</p>
    ),
    [BLOCKS.OL_LIST]: (_node, children) => (
      <ol className="mb-4 list-decimal pl-6">{children}</ol>
    ),
    [BLOCKS.UL_LIST]: (_node, children) => (
      <ul className="mb-4 list-disc pl-6">{children}</ul>
    ),

    [BLOCKS.LIST_ITEM]: (node, children) => (
      <li className="mb-1">{children}</li>
    ),
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const { gatsbyImageData, title } = node.data.target
      return (
        <GatsbyImage
          className="mt-12"
          image={getImage(gatsbyImageData)}
          alt={title}
        />
      )
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const { __typename, formId, table, embedId, code } = node.data.target
      useEffect(() => {
        const script = document.createElement('script')
        script.src = '//js.hsforms.net/forms/embed/v2.js'
        document.body.appendChild(script)

        script.addEventListener('load', () => {
          // @TS-ignore
          if (window.hbspt) {
            // @TS-ignore
            window.hbspt.forms.create({
              region: 'na1',
              portalId: '7429930',
              formId: formId,
              target: '#hubspotForm',
            })
          }
        })
      }, [node])

      if (__typename === 'ContentfulHubspotFormEmbed') {
        return <div id="hubspotForm" className="bg-default-1000 p-m"></div>
      }
      if (__typename === 'ContentfulTables') {
        return (
          <div
            className="richtext-table"
            dangerouslySetInnerHTML={{
              __html: table.childMarkdownRemark.html,
            }}
          ></div>
        )
      }
      if (__typename === 'ContentfulYouTubeEmbedLink') {
        return (
          <iframe
            className="aspect-video w-full"
            src={`https://www.youtube.com/embed/${embedId}`}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          ></iframe>
        )
      }
      if (__typename === 'ContentfulEmbedCode') {
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: code.code,
            }}
          ></div>
        )
      }
    },
  },
  renderText: (text) => {
    return text.split('\n').reduce((children, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment]
    }, [])
  },
}

export const focusRing = tv({
  base: 'outline outline-primary-600 outline-offset-2',
  variants: {
    isFocusVisible: {
      false: 'outline-0',
      true: 'outline-2',
    },
  },
})

export function composeTailwindRenderProps<T>(
  className: string | ((v: T) => string) | undefined,
  tw: string
): string | ((v: T) => string) {
  return composeRenderProps(className, (className) => twMerge(tw, className))
}
